// Typed tracking-plan SDK (github.com/AsthaBhopal/analytics_schema) wired to
// the existing RudderStack transport.
//
// Every event goes through the generated, compiler-checked methods on
// `analytics` — one method per event, enums for every governed value,
// payload null-filled to the full dictionary and stamped with
// schema_version. There is no other way to emit an event.
//
// Adding an event or enum value = PR to the analytics_schema repo (one-line
// proto edit; codegen + release are automated), then bump the package here.
import {
  ProductType,
  RupeezyAnalytics,
  Screen,
  Source,
  TransactionType,
  productTypeFromWire,
} from "@asthabhopal/tracking-plan";
import { currentDeviceType, rudderIdentify, rudderTrack } from "./rudder";
import store from "../store";

// Same canonical app name rudder.ts stamps on every event — all Rupeezy apps
// share one RudderStack source, so this is how tv-terminal rows are told apart.
const APP_NAME = "tv-terminal";
// Build commit — injected by buildspec.yaml (same source rudder.ts uses).
const APP_VERSION = process.env.REACT_APP_COMMIT_VERSION || "";

// The redux state is loosely typed at this boundary; narrow the one path
// we read.
function currentApplicationId(): string | undefined {
  const state = store.getState() as {
    authUser?: { data?: { applicationId?: string } | null };
  };
  return state.authUser?.data?.applicationId;
}

/**
 * Typed tracking surface — one generated method per event in the plan.
 *
 * Constructed WITHOUT store access: the slices this store is built from call
 * `analytics`, so store ⇄ trackingPlan is a circular import and `store` is
 * still undefined while this module body runs. `initAnalytics()` (called once
 * from App.tsx) fills in `applicationId` and keeps it fresh.
 */
export const analytics = new RupeezyAnalytics({
  track: rudderTrack,
  context: {
    appName: APP_NAME,
    deviceType: currentDeviceType(),
    appVersion: APP_VERSION,
  },
});

/**
 * Binds the current user to the analytics session (RudderStack identify).
 * Logout resets identity via rudderReset() in authService.
 */
export function identify(userId: string, traits: Record<string, unknown> = {}) {
  if (!userId) return;
  rudderIdentify(userId, traits);
}

/**
 * Keeps application_id in the global context in step with auth state,
 * refreshed once per auth change. Must run after the store module has
 * finished initialising (see the circular-import note on `analytics`).
 */
export function initAnalytics(): void {
  if (typeof window === "undefined") return;
  let lastApplicationId = currentApplicationId();
  const refresh = () => {
    analytics.updateContext({
      appName: APP_NAME,
      deviceType: currentDeviceType(),
      appVersion: APP_VERSION,
      applicationId: lastApplicationId,
    });
  };
  refresh();
  store.subscribe(() => {
    const applicationId = currentApplicationId();
    if (applicationId !== lastApplicationId) {
      lastApplicationId = applicationId;
      refresh();
    }
  });
}

// String -> enum conversion lives in the SDK as generated per-enum parsers
// (exchangeFromWire, varietyFromWire, ...) — import them from
// @asthabhopal/tracking-plan directly. The helpers below cover the two
// app-side jobs the parsers deliberately don't: Vortex payload quirks and
// free-form label munging.

/** Snake-cases free-form label text ("Trending News" -> "trending_news"). */
export function snake(v: unknown): string | undefined {
  const raw = String(v ?? "").trim();
  if (!raw) return undefined;
  return raw
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2") // camelCase -> camel_Case
    .replace(/[\s.,/-]+/g, "_")
    .toLowerCase()
    .replace(/_(tap|tapped|click|clicked|view|viewed|launch|btn|button)$/g, "")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

/** Vortex order side (1/2, "Buy"/"Sell") -> TransactionType. */
export function sideFromPayload(v: unknown): TransactionType | undefined {
  if (v === 1 || v === "1") return TransactionType.BUY;
  if (v === 2 || v === "2") return TransactionType.SELL;
  const s = String(v ?? "").toLowerCase();
  if (s.includes("buy")) return TransactionType.BUY;
  if (s.includes("sell")) return TransactionType.SELL;
  return undefined;
}

/**
 * Vortex product strings -> ProductType. Folds API aliases the tracking
 * plan deliberately doesn't know: CNC/CARRYFORWARD collapse to delivery
 * (matching order placement), BTST is the T+5 product, PAY_LATER is MTF.
 */
export function productFromPayload(v: unknown): ProductType | undefined {
  const s = String(v ?? "").toLowerCase();
  if (!s) return undefined;
  if (s.includes("intra")) return ProductType.INTRADAY;
  if (s.includes("deliv") || s === "cnc" || s.includes("carry")) return ProductType.DELIVERY;
  if (s.includes("mtf") || s.includes("pay_later") || s.includes("pay later")) return ProductType.MTF;
  if (s.includes("btst") || s === "t5" || s.includes("t+5")) return ProductType.T5;
  return productTypeFromWire(s);
}

/**
 * Canonical symbol from a TradingView-style symbol string. Strips the
 * "NSE:"/"BSE:" venue prefix (the exchange rides in its own property),
 * uppercases, and collapses whitespace so "TATASTEEL 26MAY 217.5 PE" and
 * "TATASTEEL26MAY217.5PE" land on one key.
 */
export function normSymbol(v: unknown): string | undefined {
  if (typeof v !== "string" && typeof v !== "number") return undefined;
  const raw = String(v).trim();
  if (!raw) return undefined;
  const withoutVenue = raw.includes(":") ? raw.slice(raw.indexOf(":") + 1) : raw;
  return withoutVenue.trim().toUpperCase().replace(/\s+/g, "") || undefined;
}

// ---------------------------------------------------------------------------
// Per-order journey attribution: every order_placed row carries WHERE the
// order journey started in `source` — a column on the row, no funnel
// stitching needed. Most entry points already declare themselves via the
// pre-order `origin` field, which broker.tsx threads through to _placeOrder;
// paths that construct orders internally (close/reverse position) set the
// consume-once hint below right before placing.
// ---------------------------------------------------------------------------

// `screen` is the surface that actually triggered placement; `source` is
// where the journey started. The two differ deliberately: buy_sell_button is
// the ChartBuySell dialog (TV's customUI.showOrderDialog), so ORDER_WINDOW —
// while a pre-order with NO origin is TV placing instantly from the chart
// (one-click/DOM trading, confirmations off), where no window ever opens.
const ORIGIN_ATTRIBUTION: Record<string, { source: Source; screen: Screen }> = {
  buy_sell_button: { source: Source.CHART, screen: Screen.ORDER_WINDOW },
  complete_order_basket: { source: Source.BASKET, screen: Screen.BASKET_DETAILS },
  single_order_basket: { source: Source.BASKET, screen: Screen.BASKET_DETAILS },
  strategy: { source: Source.STRATEGY, screen: Screen.STRATEGY },
};

/** Pre-order `origin` string -> order journey attribution. */
export function attributionFromOrigin(origin: unknown): { source: Source; screen: Screen } | undefined {
  return ORIGIN_ATTRIBUTION[String(origin ?? "")];
}

let orderSourceHint: Source | undefined;
export function setOrderSourceHint(source: Source | undefined): void {
  orderSourceHint = source;
}
/** Reads AND clears the hint, so it can't leak onto an unrelated order. */
export function takeOrderSourceHint(): Source | undefined {
  const source = orderSourceHint;
  orderSourceHint = undefined;
  return source;
}
